import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { KnowledgeService } from '../../../no-coding/knowledge/services/knowledge.service';
import { LoadingService } from '../../../core/services/loading.service';
import { ToastService } from '../../../coding/refactor/services/toast.service';
import { RESPONSE_STATUS } from '../../../core/constants/enums';
import { SharePointSiteInfo } from '../../../core/models/sharepoint-site-info';
import { SharePointDriveInfo } from '../../../core/models/sharepoint-drive-info';
import { SharePointFileInfo } from '../../../core/models/sharepoint-file-info';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { KNOWLEDGE_SET } from '../upload-files/upload-configs';

export type SharePointViewStep = 'sites' | 'drives' | 'files' | 'preview';

/**
 * Extensiones permitidas para importar (solo estos ficheros se subirán).
 * Se sincronizan con las del Gestor de Conocimiento a través de KNOWLEDGE_SET
 * (única fuente de verdad en upload-configs.ts). El texto mostrado en la UI usa
 * la clave i18n SHAREPOINT.ALLOWED_FILE_EXTENSIONS_MESSAGE.
 */
export const ALLOWED_FILE_EXTENSIONS = KNOWLEDGE_SET.split(',');

export interface FileToImport {
  driveId: string;
  fileId: string;
  name: string;
  path?: string;
  /** Si false, el fichero no se importará por extensión no permitida */
  allowed?: boolean;
}

export interface BreadcrumbEntry {
  label: string;
  step: SharePointViewStep;
  // Si estamos en vista files: -1 = raíz del drive, 0/1/2... = hasta qué carpeta vamos
  folderIndex?: number;
}

@Component({
  selector: 'app-sharepoint-navigator',
  templateUrl: './sharepoint-navigator.component.html',
  styleUrls: ['./sharepoint-navigator.component.scss'],
  standalone: false
})
export class SharePointNavigatorComponent implements OnInit {
  @Input() projectId!: string;

  viewStep: SharePointViewStep = 'sites';
  searchQuery = '';
  loading = false;
  selectedItems: Set<string> = new Set();

  sites: SharePointSiteInfo[] = [];
  /** Lista de sites mostrada (filtrada por searchQuery); se actualiza al escribir o al cargar. */
  filteredSitesList: SharePointSiteInfo[] = [];
  drives: SharePointDriveInfo[] = [];
  fileItems: SharePointFileInfo[] = [];

  selectedSite: SharePointSiteInfo | null = null;
  selectedDrive: SharePointDriveInfo | null = null;
  folderStack: { id: string; name: string }[] = [];

  /** Lista de ficheros a importar (tras expandir carpetas); se muestra en vista preview antes de importar */
  filesToImportPreview: FileToImport[] = [];

  breadcrumbs: BreadcrumbEntry[] = [{ label: 'Sites', step: 'sites' }];

  constructor(
    private popoverController: PopoverController,
    private knowledgeService: KnowledgeService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadSites();
  }

  get isSearchMode(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  /** Sites filtrados por búsqueda; usar filteredSitesList en template (se actualiza al escribir/cargar). */
  get filteredSites(): SharePointSiteInfo[] {
    return this.filteredSitesList;
  }

  /** Actualiza filteredSitesList según searchQuery y sites. Llamar al cargar sites y al cambiar el input. */
  applySitesFilter(): void {
    const q = (this.searchQuery ?? '').trim().toLowerCase();
    if (q === '') {
      this.filteredSitesList = this.sites.length > 0 ? [...this.sites] : [];
    } else {
      this.filteredSitesList = this.sites.filter((s) => this.siteMatchesQuery(s, q));
    }
    this.cdr.markForCheck();
  }

  /** Llamar desde (ngModelChange) del input de búsqueda para que el filtro se aplique al escribir. */
  onSearchQueryChange(value: string): void {
    this.searchQuery = value ?? '';
    this.applySitesFilter();
  }

  /**
   * Nombre visible del site (displayName || name). Misma lógica en template y en filtro.
   */
  getSiteDisplayName(site: SharePointSiteInfo): string {
    const s = site as unknown as Record<string, unknown>;
    const str = (key: string) => (typeof s?.[key] === 'string' ? (s[key] as string).trim() : '');
    return str('displayName') || str('DisplayName') || str('name') || str('Name') || '';
  }

  /** Coincide solo si el nombre visible del site contiene la query (sensible a lo que se muestra). */
  private siteMatchesQuery(site: SharePointSiteInfo, q: string): boolean {
    const name = this.getSiteDisplayName(site);
    if (!name) return false;
    return name.toLowerCase().includes(q);
  }

  /** Segmentos del nombre para resaltar la búsqueda: cada parte es texto normal o coincidencia. */
  getHighlightedNameSegments(site: SharePointSiteInfo): { text: string; match: boolean }[] {
    const name = this.getSiteDisplayName(site);
    const q = (this.searchQuery ?? '').trim().toLowerCase();
    if (!q || !name) return [{ text: name || '', match: false }];
    const lower = name.toLowerCase();
    const segments: { text: string; match: boolean }[] = [];
    let pos = 0;
    while (pos < name.length) {
      const idx = lower.indexOf(q, pos);
      if (idx === -1) {
        segments.push({ text: name.slice(pos), match: false });
        break;
      }
      if (idx > pos) segments.push({ text: name.slice(pos, idx), match: false });
      segments.push({ text: name.slice(idx, idx + q.length), match: true });
      pos = idx + q.length;
    }
    return segments;
  }

  loadSites(): void {
    this.loading = true;
    this.viewStep = 'sites';
    this.updateBreadcrumbs();
    this.knowledgeService.getSharePointSites().subscribe({
      next: (list) => {
        this.sites = list || [];
        this.applySitesFilter();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error cargando sites de SharePoint:', err);
        this.toastService.showToast('Error al cargar los sites de SharePoint', RESPONSE_STATUS.ERROR);
        this.loading = false;
        this.sites = [];
      }
    });
  }

  /** Aplica/actualiza el filtro de búsqueda (la lista visible usa filteredSites). */
  searchSites(): void {
    if (!this.searchQuery.trim()) {
      this.loadSites();
      return;
    }
    // Búsqueda en cliente: si ya tenemos sites cargados, no hace falta recargar
    if (this.sites.length === 0) {
      this.loadSites();
    }
  }

  selectSite(site: SharePointSiteInfo): void {
    this.selectedSite = site;
    this.loading = true;
    this.viewStep = 'drives';
    this.updateBreadcrumbs();
    this.knowledgeService.getSharePointDrives(site.id).subscribe({
      next: (list) => {
        this.drives = list || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error cargando drives de SharePoint:', err);
        this.toastService.showToast('Error al cargar los drives del site', RESPONSE_STATUS.ERROR);
        this.loading = false;
        this.drives = [];
      }
    });
  }

  selectDrive(drive: SharePointDriveInfo): void {
    this.selectedDrive = drive;
    this.selectedItems.clear();
    this.folderStack = [];
    this.loadDriveFiles();
  }

  loadDriveFiles(): void {
    if (!this.selectedDrive) return;
    this.loading = true;
    this.viewStep = 'files';
    this.updateBreadcrumbs();
    const obs = this.folderStack.length === 0
      ? this.knowledgeService.getSharePointDriveFiles(this.selectedDrive.id)
      : this.knowledgeService.getSharePointFolderFiles(
          this.selectedDrive.id,
          this.folderStack[this.folderStack.length - 1].id
        );
    obs.subscribe({
      next: (list) => {
        this.fileItems = list || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error cargando ficheros de SharePoint:', err);
        this.toastService.showToast('Error al cargar el directorio de SharePoint', RESPONSE_STATUS.ERROR);
        this.loading = false;
        this.fileItems = [];
      }
    });
  }

  /** Clic en la fila: entrar si es carpeta, marcar/desmarcar si es fichero. */
  onItemRowClick(item: SharePointFileInfo): void {
    if (this.isFolder(item)) {
      this.navigateToFolder(item);
    } else {
      this.toggleItemSelection(item);
    }
  }

  navigateToFolder(item: SharePointFileInfo): void {
    this.selectedItems.clear();
    if (!this.isFolder(item) || !this.selectedDrive) return;
    this.folderStack.push({ id: item.id, name: item.name });
    this.loadDriveFiles();
  }

  navigateToBreadcrumb(entry: BreadcrumbEntry): void {
    this.selectedItems.clear();
    if (entry.step === 'sites') {
      this.viewStep = 'sites';
      this.selectedSite = null;
      this.selectedDrive = null;
      this.folderStack = [];
      this.loadSites();
      return;
    }
    if (entry.step === 'drives') {
      this.viewStep = 'drives';
      this.selectedDrive = null;
      this.folderStack = [];
      this.updateBreadcrumbs();
      return;
    }
    if (entry.step === 'files' && entry.folderIndex !== undefined) {
      this.viewStep = 'files';
      this.folderStack = entry.folderIndex < 0 ? [] : this.folderStack.slice(0, entry.folderIndex + 1);
      this.loadDriveFiles();
    }
  }

  goUp(): void {
    if (this.viewStep === 'files' && this.folderStack.length > 0) {
      this.folderStack.pop();
      this.loadDriveFiles();
      return;
    }
    if (this.viewStep === 'drives') {
      this.viewStep = 'sites';
      this.selectedSite = null;
      this.updateBreadcrumbs();
      this.loadSites();
      return;
    }
    if (this.viewStep === 'files' && this.folderStack.length === 0) {
      this.viewStep = 'drives';
      this.selectedDrive = null;
      this.updateBreadcrumbs();
      return;
    }
  }

  updateBreadcrumbs(): void {
    const crumbs: BreadcrumbEntry[] = [{ label: 'Sites', step: 'sites' }];
    if (this.selectedSite) {
      crumbs.push({ label: this.selectedSite.displayName || this.selectedSite.name, step: 'drives' });
    }
    if (this.selectedDrive) {
      crumbs.push({
        label: this.selectedDrive.name,
        step: 'files',
        folderIndex: -1
      });
      this.folderStack.forEach((folder, index) => {
        crumbs.push({
          label: folder.name,
          step: 'files',
          folderIndex: index
        });
      });
    }
    this.breadcrumbs = crumbs;
  }

  isFolder(item: SharePointFileInfo): boolean {
    return !!item.folder;
  }

  toggleItemSelection(item: SharePointFileInfo): void {
    if (this.selectedItems.has(item.id)) {
      this.selectedItems.delete(item.id);
    } else {
      this.selectedItems.add(item.id);
    }
    this.cdr.markForCheck();
  }

  isItemSelected(itemId: string): boolean {
    return this.selectedItems.has(itemId);
  }

  /** Comprueba si el nombre del fichero tiene una extensión permitida para importar. */
  isAllowedExtension(fileName: string): boolean {
    if (!fileName || !fileName.includes('.')) return false;
    const parts = fileName.split('.');
    const ext = parts.length > 1 ? '.' + (parts.pop() ?? '').toLowerCase() : '';
    return ALLOWED_FILE_EXTENSIONS.includes(ext);
  }

  /** Número de ficheros en la preview que sí se importarán (extensión permitida). */
  get allowedFilesCount(): number {
    return this.filesToImportPreview.filter((f) => f.allowed !== false).length;
  }

  /** Recorre una carpeta y todas las subcarpetas, devolviendo la lista plana de ficheros. */
  private collectFilesFromFolder(driveId: string, folderId: string, pathPrefix: string): Observable<FileToImport[]> {
    return this.knowledgeService.getSharePointFolderFiles(driveId, folderId).pipe(
      switchMap((items) => {
        const files: FileToImport[] = [];
        const subfolders: Observable<FileToImport[]>[] = [];
        for (const item of items || []) {
          if (this.isFolder(item)) {
            subfolders.push(
              this.collectFilesFromFolder(driveId, item.id, pathPrefix + item.name + '/')
            );
          } else {
            files.push({
              driveId,
              fileId: item.id,
              name: item.name,
              path: pathPrefix + item.name
            });
          }
        }
        if (subfolders.length === 0) return of(files);
        return forkJoin(subfolders).pipe(
          map((nested) => files.concat(...nested))
        );
      })
    );
  }

  /** Construye la lista completa de ficheros: los seleccionados directamente más todos los de las carpetas seleccionadas. */
  private collectAllFilesToImport(): Observable<FileToImport[]> {
    if (!this.selectedDrive) return of([]);
    const driveId = this.selectedDrive.id;
    const selected = this.fileItems.filter((item) => this.selectedItems.has(item.id));
    const directFiles: FileToImport[] = [];
    const folderObservables: Observable<FileToImport[]>[] = [];
    const pathPrefix = this.folderStack.map((f) => f.name + '/').join('');
    for (const item of selected) {
      if (this.isFolder(item)) {
        folderObservables.push(
          this.collectFilesFromFolder(driveId, item.id, pathPrefix + item.name + '/')
        );
      } else {
        directFiles.push({
          driveId,
          fileId: item.id,
          name: item.name,
          path: pathPrefix + item.name
        });
      }
    }
    if (folderObservables.length === 0) return of(directFiles);
    return forkJoin(folderObservables).pipe(
      map((nested) => directFiles.concat(...nested))
    );
  }

  addSelectedFiles(): void {
    if (this.selectedItems.size === 0) {
      this.toastService.showToast('Selecciona al menos un fichero o carpeta', RESPONSE_STATUS.WARNING);
      return;
    }
    if (!this.selectedDrive) {
      this.toastService.showToast('Error: no hay drive seleccionado', RESPONSE_STATUS.ERROR);
      return;
    }
    this.loadingService.showLoader();
    this.collectAllFilesToImport().subscribe({
      next: (list) => {
        this.loadingService.hideLoader();
        if (list.length === 0) {
          this.toastService.showToast('No se encontraron ficheros en la selección', RESPONSE_STATUS.WARNING);
          return;
        }
        this.filesToImportPreview = list.map((f) => ({
          ...f,
          allowed: this.isAllowedExtension(f.name)
        }));
        this.viewStep = 'preview';
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loadingService.hideLoader();
        this.toastService.showToast('Error al listar el contenido de las carpetas', RESPONSE_STATUS.ERROR);
        console.error('Error recopilando ficheros:', err);
      }
    });
  }

  goBackFromPreview(): void {
    this.viewStep = 'files';
    this.filesToImportPreview = [];
    this.cdr.markForCheck();
  }

  confirmImport(): void {
    if (this.filesToImportPreview.length === 0 || !this.selectedDrive) return;
    const toImport = this.filesToImportPreview.filter((f) => f.allowed !== false);
    if (toImport.length === 0) {
      this.toastService.showToast(
        this.translate.instant('SHAREPOINT.ALLOWED_FILE_EXTENSIONS_MESSAGE'),
        RESPONSE_STATUS.WARNING
      );
      return;
    }
    const driveId = this.selectedDrive.id;
    const fileIds = toImport.map((f) => f.fileId);
    this.loadingService.showLoader();
    if (fileIds.length === 1) {
      this.knowledgeService.importSharePointFile({
        projectId: this.projectId,
        driveId,
        itemId: fileIds[0]
      }).subscribe({
        next: (response) => {
          this.loadingService.hideLoader();
          this.toastService.showToast(`Fichero "${response.name}" importado correctamente`, RESPONSE_STATUS.OK);
          this.popoverController.dismiss({
            result: true,
            taskId: response.taskId,
            addedFiles: { totalRequested: 1, successCount: 1, failCount: 0, successful: [response], failed: [] }
          });
        },
        error: (err) => {
          this.loadingService.hideLoader();
          this.toastService.showToast('Error al importar el fichero de SharePoint', RESPONSE_STATUS.ERROR);
          console.error('Error importando fichero:', err);
        }
      });
    } else {
      this.knowledgeService.importSharePointFilesBatch({
        projectId: this.projectId,
        driveId,
        itemIds: fileIds
      }).subscribe({
        next: (response) => {
          this.loadingService.hideLoader();
          const msg = response.failCount > 0
            ? `Importados ${response.successCount} de ${response.totalRequested} fichero(s). ${response.failCount} error(es).`
            : `Se importaron ${response.successCount} fichero(s) correctamente`;
          this.toastService.showToast(msg, response.failCount > 0 ? RESPONSE_STATUS.WARNING : RESPONSE_STATUS.OK);
          this.popoverController.dismiss({ result: true, addedFiles: response });
        },
        error: (err) => {
          this.loadingService.hideLoader();
          this.toastService.showToast('Error al importar ficheros de SharePoint', RESPONSE_STATUS.ERROR);
          console.error('Error importando ficheros:', err);
        }
      });
    }
  }

  cancel(): void {
    this.popoverController.dismiss({ result: false });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  formatDate(value: Date | string | undefined): string {
    if (!value) return '';
    const d = typeof value === 'string' ? new Date(value) : value;
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  }
}
